import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, forwardRef } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IConfigurableEvent } from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Store } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { V12RetailFinance } from '@df/module-V12/v12-retail-finance.namespace';
import { AbIfDirective } from '@df/module-ab/ab-if.directive';
import { ImageModule } from '@df/module-image/image.module';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { OptionImagePipe } from '@df/ui/common/option-image.pipe';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ScrollToDirective } from '@df/ui/util/scroll/scroll-to.directive';
import { BasketItemQtyComponent } from '../../../basket/item-qty/basket-item-qty.component';
import { ReviewsIORatingComponent } from '../../../module/reviews-io/components/reviews-io-rating.component';
import { FinanceFromComponent } from '../../../module/v12-retail-finance/v12-retail-finance-from.component';
import { SeeRangeinStoreProductStatusComponent } from '../../see-range-in-store/product-status/see-range-in-store-product-status.component';
import { SeeRangeInStore } from '../../see-range-in-store/see-range-in-store';
import { ProductInfoDeliveryComponent } from '../info-delivery/product-info-delivery.component';
import { PdpTrustMessagesComponent } from '../pdp-trust-messages/pdp-trust-messages.component';
import { ENywItemCategoryCodeNh, EProductStoreDepartmentCode, NProductViewLayout, Product } from '../product.decorator';
import { SPECIFICATION_ATTRIBUTES_CODES } from '../product.service';
import { ProductStockLevelsComponent } from '../stock-levels/product-stock-levels.component';
import { ProductViewAddToBasketComponent } from '../view-add-to-basket/product-view-add-to-basket.component';
import { ProductViewCrosssellProductsColorComponent } from '../view-crosssell-products/color/product-view-crosssell-products-color.component';
import { ProductViewCrosssellProductsSizeComponent } from '../view-crosssell-products/size/product-view-crosssell-products-size.component';
import { ProductViewPriceComponent } from '../view-price/product-view-price.component';
import { ProductViewSamplesComponent } from '../view-samples/product-view-samples.component';
import { ProductViewSpecificationIcmsComponent } from '../view-specification-icms/product-view-specification-icms.component';
import { ProductViewSpecificationComponent } from '../view-specification/product-view-specification.component';
import { ProductViewTreeComponent } from '../view-tree/product-view-tree.component';
import { ProductViewUpsellProductsComponent } from '../view-upsell-products/product-view-upsell-products.component';
import { ProductViewWishlistToggleComponent } from '../view-wishlist-toggle/product-view-wishlist-toggle.component';
import { ProductViewComponent } from '../view/product-view.component';
import { ProductWarrantyComponent } from '../warranty/product-warranty.component';
import { ProductViewConfigurableOptionsComponent } from './../view-configurable-options/product-view-configurable-options.component';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            vfinance: V12RetailFinance.IPaymentMethodConfig;
        }
    }
}

@Component({
    selector: 'product-view-details',
    templateUrl: 'product-view-details.html',
    styleUrls: ['./product-view-details.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ProductViewSpecificationComponent,
        ProductInfoDeliveryComponent,
        ProductStockLevelsComponent,
        ProductViewAddToBasketComponent,
        BasketItemQtyComponent,
        ProductViewUpsellProductsComponent,
        OptionImagePipe,
        ProductViewPriceComponent,
        ProductViewWishlistToggleComponent,
        ProductViewTreeComponent,
        PdpTrustMessagesComponent,
        ProductWarrantyComponent,
        FinanceFromComponent,
        TranslationModule,
        RouterLink,
        ImageModule,
        ProductViewSpecificationIcmsComponent,
        ProductViewCrosssellProductsSizeComponent,
        ProductViewCrosssellProductsColorComponent,
        CommonModule,
        ProductViewSamplesComponent,
        ProductViewConfigurableOptionsComponent,
        SeeRangeinStoreProductStatusComponent,
        ReviewsIORatingComponent,
        IfSizeDirective,
        ScrollToDirective,
        AbIfDirective
    ]
})
export class ProductViewDetailsComponent extends UiComponent implements OnChanges {
    hasVisibleOptions = false;
    specificationAttributesCodes = SPECIFICATION_ATTRIBUTES_CODES;
    selectedQty?: number;
    /**
     * Currently viewed product
     */
    @Input()
    product?: Product<IProductDataV3>;
    /**
     * Sepecification sections
     */
    @Input()
    specSections?: string[];
    @Input()
    content?: NProductViewLayout.IBlockDataContent;
    constructor(
        @Inject(forwardRef(() => ProductViewComponent)) readonly productView: ProductViewComponent,
        private storeResource: StoreResource
    ) {
        super();
    }
    override ngOnChanges(changes: any): void {
        super.ngOnChanges(changes);
        if (changes.product) {
            if (!this.displayQtySelector) {
                this.selectedQty = 1;
            }
            if (this.isClearanceView) {
                this.product?.loadSeeRangeInStoreModel().finally(() => this.detectChanges());
            }
        }
    }
    get displayQtySelector(): boolean {
        if (this.product?.data) {
            return (
                this.product.data.storedepartmentcode === EProductStoreDepartmentCode.accessories ||
                this.product.data.nyw_item_category_code_nh === ENywItemCategoryCodeNh.diningChairs ||
                this.product.data.nyw_item_category_code_nh === ENywItemCategoryCodeNh.barStools
            );
        }
        return false;
    }
    get isClearanceView(): boolean {
        return !!this.product?.isClearance;
    }
    get isClearanceSold(): boolean {
        return !!this.product?.isClearanceSold;
    }
    private get seeRangeInStoreModel(): SeeRangeInStore | undefined {
        return this.product?.seeRangeInStoreModel;
    }
    private get storeId(): number | undefined {
        return this.seeRangeInStoreModel?.storepickupId;
    }
    get store(): Store | undefined {
        if (this.storeId) {
            return this.storeResource.getExisting(this.storeId);
        }
        return undefined;
    }
    onProductConfigurablesInit($event: IConfigurableEvent): void {
        const data = $event.data;
        if ((!data || !Object.values(data).filter(i => !!i).length) && this.product?.isConfigurable) {
            const defaultChild = this.product?.children.find(i => !!(i as Product<IProductDataV3>).data?.default_variant);
            if (defaultChild) {
                const selectionObject = defaultChild.createSelectionObject();
                if (selectionObject) {
                    this.productView.matchExactly(selectionObject);
                    return;
                }
            }
        }
        const vm = $event.vm as ProductViewConfigurableOptionsComponent;
        this.hasVisibleOptions = !!vm.visibleAttributes?.length;
        this.detectChanges();
        this.productView.matchExactly($event.data);
    }
    get displayColorCrossSells(): boolean {
        if (this.product?.hasColorCrosssells) {
            if (this.product?.parent?.isConfigurable) {
                return !this.hasVisibleOptions;
            }
            return true;
        }
        return false;
    }
    openZendeskChat(): void {
        (<any>window)?.zE?.activate();
    }

    get variantAttribute1(): string | undefined {
        return this.product?.variantAttribute1;
    }

    asString(value: string | number): string {
        return value as string;
    }
}
